import { useEffect } from 'react';
import Reveal from '../components/Reveal';

// ─── NAVIGATION ──────────────────────────────────────────────────────────────

function Brand({ small = false }: {small?: boolean;}) {
  return (
    <div className="brand">
      <b style={{ fontWeight: 600 }}>
        Lu<span className="accent">La</span>Mo
      </b>
      {!small && <small>Lumière · Lâcher prise · Mouvement</small>}
    </div>);

}

function Nav() {
  const links = [
  { href: '#signature', label: 'La signature' },
  { href: '#about', label: 'À propos' },
  { href: '#approche', label: 'Approche' },
  { href: '#contact', label: 'Prendre contact', cta: true }];


  return (
    <nav>
      <div className="wrap nav-inner">
        <Brand />
        <div className="nav-links">
          {links.map((l) =>
          <a key={l.href} href={l.href} className={l.cta ? 'nav-cta' : undefined}>
              {l.label}
            </a>
          )}
        </div>
      </div>
    </nav>);

}

// ─── HERO ────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <header className="hero">
      <svg className="ridge" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true">
        <path
          d="M0,120 L0,78 C220,40 360,96 560,70 C760,44 880,18 1080,58 C1240,90 1340,72 1440,84 L1440,120 Z"
          fill="#081627" />

      </svg>
      <div className="wrap">
        <p className="eyebrow hero-eyebrow">Partenaire stratégique du capital humain</p>
        <h1>
          <span>Certaines entreprises grandissent.</span>
          <span>D'autres prennent de la valeur.</span>
          <span>
            La différence est souvent <span className="gold ital">humaine</span>.
          </span>
        </h1>
        <p className="lead">
          Les freins à la croissance, à la transformation et à la transmission sont rarement
          stratégiques ou financiers. Ils sont humains. C'est là que j'interviens.
        </p>
        <div className="cta-row">
          <a href="#contact" className="btn btn-primary">
            Échangeons sur vos enjeux →
          </a>
          <a href="#approche" className="btn btn-ghost">
            Découvrir l'approche
          </a>
        </div>

        <div className="pillars">
          <div className="pillar">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="9" cy="8" r="3" />
              <path d="M3 20c0-3 2.5-5 6-5s6 2 6 5" />
              <path d="M16 7l3 0M17.5 5.5l0 3" />
            </svg>
            <span>Leadership</span>
          </div>
          <div className="pillar">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 9a8 8 0 0 1 13-3" />
              <path d="M20 15a8 8 0 0 1-13 3" />
              <path d="M17 3v3h-3M7 21v-3h3" />
            </svg>
            <span>Transmission</span>
          </div>
          <div className="pillar">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="7" r="3" />
              <path d="M5 20c0-4 3-6 7-6s7 2 7 6" />
              <circle cx="5" cy="11" r="1.6" />
              <circle cx="19" cy="11" r="1.6" />
            </svg>
            <span>Engagement</span>
          </div>
          <div className="pillar">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 21c-4-4-7-7-7-11a7 7 0 0 1 14 0c0 4-3 7-7 11Z" />
              <circle cx="12" cy="10" r="2.4" />
            </svg>
            <span>Dynamiques humaines</span>
          </div>
        </div>
      </div>
    </header>);

}

// ─── SIGNATURE ───────────────────────────────────────────────────────────────

function SignatureSection() {
  const sigs = [
  {
    big: 'Lu',
    title: 'Lumière',
    text: "Rendre visibles les dynamiques humaines qui freinent, ou portent, la croissance. Celles que les chiffres ne montrent pas."
  },
  {
    big: 'La',
    title: 'Lâcher prise',
    text: "Aider le dirigeant à déléguer, à ne plus tout porter seul, à rendre l'entreprise capable d'exister et de prospérer au-delà de lui."
  },
  {
    big: 'Mo',
    title: 'Mouvement',
    text: "Passer de l'intention à la transformation réelle, et mobiliser durablement les équipes autour d'un cap partagé."
  }];


  return (
    <section className="signature" id="signature">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">La signature LuLaMo</span>
          <h2>Trois mouvements pour révéler la valeur humaine</h2>
        </div>
        <div className="divider">
          <span className="ln" />
          <span className="dot" />
          <span className="ln r" />
        </div>
        <div className="sig-grid">
          {sigs.map((s) =>
          <Reveal key={s.big} className="sig">
              <div className="big">{s.big}</div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </Reveal>
          )}
        </div>
      </div>
    </section>);

}

// ─── SITUATIONS ──────────────────────────────────────────────────────────────

function SituationsSection() {
  const sits = [
  {
    pb: 'Vous portez seul une part trop lourde des décisions, et tout ralentit dès que vous vous éloignez.',
    rs: 'Une organisation plus autonome, qui tient sans dépendre de vous.'
  },
  {
    pb: 'Votre entreprise a du potentiel, mais peine à franchir une nouvelle étape.',
    rs: 'Un développement plus fluide et mieux maîtrisé.'
  },
  {
    pb: 'Vos savoir-faire clés reposent sur une poignée de personnes.',
    rs: 'La transmission sécurisée de vos compétences stratégiques.'
  },
  {
    pb: 'Vos équipes avancent, mais sans réel alignement.',
    rs: 'Une cohésion et une efficacité collective retrouvées.'
  },
  {
    pb: 'Une transformation en cours génère résistances et tensions.',
    rs: 'Une adhésion réelle, et des changements qui tiennent dans la durée.'
  }];


  return (
    <section className="situations">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Les enjeux des dirigeants</span>
          <h2>Reconnaissez-vous votre situation ?</h2>
        </div>
        <Reveal as="p" className="sit-intro">
          La plupart des dirigeants que je rencontre arrivent avec la même intuition : quelque
          chose freine, mais ce n'est ni la stratégie, ni le marché, ni les chiffres.
        </Reveal>
        <div className="sit-list">
          {sits.map((s, i) =>
          <Reveal key={i} className="sit">
              <div className="pb">{s.pb}</div>
              <div className="ar">→</div>
              <div className="rs">{s.rs}</div>
            </Reveal>
          )}
        </div>
        <Reveal as="p" className="sit-foot">
          Si l'une de ces situations vous parle, c'est précisément là que j'interviens.
        </Reveal>
      </div>
    </section>);

}

// ─── LECTURE ─────────────────────────────────────────────────────────────────

function LectureSection() {
  const rows = [
  { see: '« Mon équipe ne fonctionne pas. »', read: 'Une dynamique relationnelle non dite qui bloque la coopération.' },
  { see: '« On grandit, mais dans le désordre. »', read: "Des priorités et une structure qui n'ont pas suivi la croissance." },
  { see: '« Si untel part, on perd tout. »', read: 'Un savoir-faire essentiel jamais transmis ni formalisé.' },
  { see: '« Mes meilleurs éléments s\'en vont. »', read: "Des potentiels qu'on n'a jamais vraiment activés." },
  { see: '« Je décide de tout, seul. »', read: "Une entreprise construite autour de vous, pas autour d'un cap." }];


  return (
    <section className="lecture">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Ma lecture</span>
          <h2>Ce que vous voyez. Ce que je lis.</h2>
        </div>
        <Reveal as="p" className="lec-intro">
          Un même problème se lit à deux niveaux. La plupart s'arrêtent au symptôme. Mon métier est
          de lire ce qui en est la cause.
        </Reveal>
        <div className="lec-wrap">
          <Reveal className="lec-head">
            <span className="h1">Ce que l'on voit</span>
            <span className="h2">Ce que je lis</span>
          </Reveal>
          {rows.map((r, i) =>
          <Reveal key={i} className="lec-row">
              <div className="lec-see">{r.see}</div>
              <div className="lec-read">{r.read}</div>
            </Reveal>
          )}
        </div>
        <Reveal as="p" className="lec-foot">
          C'est en travaillant la cause, et non le symptôme, que la valeur se libère.
        </Reveal>
      </div>
    </section>);

}

// ─── À PROPOS ────────────────────────────────────────────────────────────────

function AboutSection() {
  const chips = ['Formatrice BFFA', 'PersProfile Manager Certifiée', 'Auditrice Eduqua', 'Executive Coach ICF'];

  return (
    <section className="about" id="about">
      <div className="wrap about-grid">
        <Reveal className="about-visual">
          <div className="portrait">
            <img
              src="https://static.lumi.new/5c/5c09ba4dab002907c0be937d9c60a192.webp"
              alt="Portrait de Sabrina Haenni"
              className="portrait-img"
              loading="lazy" />
          </div>
          <div className="stat">
            <b>20+</b>
            <span>années d'expérience aux côtés des dirigeants et de leurs équipes</span>
          </div>
        </Reveal>
        <Reveal>
          <p className="role">À propos</p>
          <h2>Vingt ans aux côtés de dirigeants, d'équipes et d'organisations en mouvement.</h2>
          <p>J'interviens auprès de dirigeants et d'organisations confrontés à des enjeux de
            croissance, de transformation ou de transmission. Mon rôle : identifier les dynamiques
            humaines qui soutiennent, ou freinent, leur développement, et les remettre au service
            de l'entreprise.

          </p>
          <p>
            Je crois davantage à la capacité de révéler les leviers invisibles d'une organisation
            qu'à l'application de méthodes standardisées. Chaque entreprise a ses propres ressources ;
            mon métier est de les activer.
          </p>
          <div className="creds">
            {chips.map((c) =>
            <span key={c} className="chip">
                {c}
              </span>
            )}
          </div>
        </Reveal>
      </div>
    </section>);

}

// ─── APPROCHE (6 étapes) ─────────────────────────────────────────────────────

function ApprocheSection() {
  const phases = [
  {
    n: '1',
    title: 'Comprendre',
    desc: 'Clarifier la demande, le contexte et le périmètre réels, avant toute solution.',
    liv: 'Une proposition de mission claire et cadrée.'
  },
  {
    n: '2',
    title: 'Analyser',
    desc: 'Lire ce qui se joue derrière le problème apparent (leadership, rôles, coopération, dépendance au dirigeant) et distinguer les symptômes des causes.',
    liv: "Un diagnostic et un rapport d'aide à la décision."
  },
  {
    n: '3',
    title: 'Agir',
    desc: "Intervenir sur les vrais leviers, au bon niveau (dirigeant, équipe de direction, managers, organisation) et ajuster en chemin.",
    liv: 'Une feuille de route opérationnelle, suivie et ajustée.'
  },
  {
    n: '4',
    title: 'Pérenniser',
    desc: "Ancrer les changements pour que l'entreprise ne retombe pas dans ses anciens fonctionnements, et mesurer les résultats.",
    liv: 'Un plan de pérennisation et un bilan des résultats.'
  }];


  return (
    <section className="approche" id="approche">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Mon approche</span>
          <h2>Une intervention structurée, en quatre phases</h2>
        </div>
        <Reveal as="p" className="approche-intro">
          L'intervention ne commence pas par une solution prédéfinie, elle commence par l'analyse du besoin réel.
        </Reveal>
        <div className="phases">
          {phases.map((p) =>
          <Reveal key={p.n} className="phase">
              <div className="phase-num">{p.n}</div>
              <h3>{p.title}</h3>
              <p className="phase-desc">{p.desc}</p>
              <div className="phase-liv">
                <span className="phase-liv-lab">Livrable</span>
                <span className="phase-liv-val">{p.liv}</span>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>);

}

// ─── QUOTE ───────────────────────────────────────────────────────────────────

function QuoteSection() {
  return (
    <section className="quote">
      <div className="wrap">
        <blockquote>
          « La valeur d'une entreprise repose encore trop souvent sur son seul dirigeant. C'est
          généralement le premier frein à sa croissance et à sa transmission. »
        </blockquote>
        <span className="src">Observation après 20 ans aux côtés des dirigeants</span>
      </div>
    </section>);

}

// ─── CONTACT ─────────────────────────────────────────────────────────────────

function ContactSection() {
  const cards = [
  { href: 'mailto:sabrina.haenni@lulamo.ch', lab: 'Email', val: 'sabrina.haenni@lulamo.ch' },
  { href: 'https://www.linkedin.com/in/sabrina-haenni-lulamo/', lab: 'LinkedIn', val: 'Voir le profil de Sabrina Haenni', external: true }];


  return (
    <section className="contact" id="contact">
      <div className="wrap">
        <div className="ct-grid">
          <Reveal>
            <h2>Échangeons sur vos enjeux</h2>
            <p className="sub firststep">
              Le meilleur point de départ est un <b>rendez-vous de 30 minutes</b>, pour clarifier vos
              besoins.
            </p>
            <p className="loc">◍ Basée à Genève</p>
          </Reveal>
          <Reveal>
            {cards.map((c, i) =>
            <a
              key={i}
              className="ct-card"
              href={c.href}
              {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                <div>
                  <span className="lab">{c.lab}</span>
                  <span className="val">{c.val}</span>
                </div>
                <span className="arr">→</span>
              </a>
            )}
          </Reveal>
        </div>
        <footer>
          <Brand small />
          <small>Partenaire stratégique du capital humain · Genève</small>
        </footer>
      </div>
    </section>);

}

// ─── PAGE PRINCIPALE ─────────────────────────────────────────────────────────

export default function Home() {
  useEffect(() => {
    document.title = 'LuLaMo — Accompagnement des dirigeants';
  }, []);

  return (
    <div className="lulamo">
      <Nav />
      <Hero />
      <SignatureSection />
      <SituationsSection />
      <LectureSection />
      <AboutSection />
      <QuoteSection />
      <ApprocheSection />
      <ContactSection />
    </div>);

}